import { FetchResult, MutationResult, TypedDocumentNode, gql, useMutation, useQuery } from '@apollo/client';
import {
  OptimizationGroupDataQuery,
  OptimizationGroupDataQueryVariables,
  OptimizationGroupDataUpdateMutation,
  OptimizationGroupDataUpdateMutationVariables,
  OptimizationRunnerMutation,
  OptimizationRunnerMutationVariables,
} from 'generated/graphql';

const optimizationGroupDataQuery: TypedDocumentNode<OptimizationGroupDataQuery, OptimizationGroupDataQueryVariables> =
  gql`
    query OptimizationGroupData($siteId: String!) {
      OptimizationGroupData(siteId: $siteId) {
        json
      }
    }
  `;

const optimizationDataUpdateMutation: TypedDocumentNode<
  OptimizationGroupDataUpdateMutation,
  OptimizationGroupDataUpdateMutationVariables
> = gql`
  mutation OptimizationGroupDataUpdate($siteId: String!, $updatedParameters: String!) {
    OptimizationGroupDataUpdate(siteId: $siteId, updatedParameters: $updatedParameters) {
      json
    }
  }
`;

const optimizationRunnerMutation: TypedDocumentNode<
  OptimizationRunnerMutation,
  OptimizationGroupDataUpdateMutationVariables
> = gql`
  mutation OptimizationRunner($siteId: String!) {
    OptimizationRunner(siteId: $siteId) {
      status
    }
  }
`;

export function useOptimizationGroupData(siteId: string) {
  const { data, loading } = useQuery(optimizationGroupDataQuery, { variables: { siteId } });

  return {
    data: data?.OptimizationGroupData,
    loadingData: loading,
  };
}

export function useOptimizationGroupDataUpdate(): [
  (siteId: string, updatedParameters: string) => Promise<FetchResult<OptimizationGroupDataUpdateMutation>>,
  MutationResult<OptimizationGroupDataUpdateMutation>,
] {
  const [update, response] = useMutation<
    OptimizationGroupDataUpdateMutation,
    OptimizationGroupDataUpdateMutationVariables
  >(optimizationDataUpdateMutation);
  return [
    (siteId, updatedParameters) =>
      update({ variables: { siteId, updatedParameters }, refetchQueries: ['OptimizationGroupData'] }),
    response,
  ];
}

export function useOptimizationRunner(siteId: string): () => Promise<FetchResult<OptimizationRunnerMutation>> {
  const [run] = useMutation<OptimizationRunnerMutation, OptimizationRunnerMutationVariables>(
    optimizationRunnerMutation,
  );
  return () => run({ variables: { siteId } });
}
